// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import PropTypes from 'prop-types';
import LabelsContext from '@tcp/core/src/components/common/context/LabelsContext';
import SafeComponent from '@tcp/core/src/components/common/hoc/withErrorBoundary';
import { StyledESpotPromoSkeleton } from '@tcp/core/src/components/features/browse/CategoryListingPage/styles/PaginatedPage.style';
import { getTileHeight } from '@tcp/core/src/components/features/browse/CategoryListingPage/container/CategoryListingPage.utils';
import { plpTileClassName } from '@tcp/core/src/components/features/browse/PlpTile/plpTileConstants';
import { StyledESpotPromoTile, StyledDamImage } from '../styles/EspotPlpPromo.style';

const getLastTitleIndex = (titleIndexesArray) => {
  return titleIndexesArray.length > 0 && titleIndexesArray[titleIndexesArray.length - 1] + 1;
};

const shouldRenderFullWidthPromo = ({ currentRowNo, isFullWidthInGridPromoHidden }) => {
  return currentRowNo && Object.keys(currentRowNo).length > 0 && !isFullWidthInGridPromoHidden;
};

const getIsGridBlockRefChildrensNotAvailable = (gridBlockRef) => {
  return !gridBlockRef?.children || !gridBlockRef?.children?.length;
};

const determineColumns = () => {
  // Determine grid columns based on viewport breakpoints
  let columns = 4; // Default for desktop
  const width = window?.innerWidth;

  if (width <= 929) columns = 3; // Tablet
  if (width <= 534) columns = 2; // Mobile
  return columns;
};

const getTileSize = ({ currentRowNo, indexes, i }) => {
  const promo = currentRowNo[indexes[i]] || {};

  const { tileSize } = promo || {};
  return tileSize;
};

const shouldUpdateRowStart = ({
  titleIndexesArray,
  nearestCategoryStartingIndex,
  adjustedIndex,
}) => {
  return titleIndexesArray.length > 0 && nearestCategoryStartingIndex <= adjustedIndex;
};

const isValidTile = (element) => element?.classList?.contains(plpTileClassName);

const getIsValidProductTileInRow = ({ i, adjustedIndex, gridBlockRef }) => {
  return i !== adjustedIndex && isValidTile(gridBlockRef.children[i]);
};

export const getEspotTileHeight = ({
  gridPosition,
  gridBlockRef,
  titleIndexesArray,
  currentRowNo,
  isFullWidthInGridPromoHidden,
  currentPageNumber,
  recordsPerPage,
}) => {
  if (getIsGridBlockRefChildrensNotAvailable(gridBlockRef)) {
    return { tileHeightValue: 0, tileBadgeHeightValue: 0 };
  }

  const columns = determineColumns();

  let adjustedIndex = gridPosition;

  let nearestCategoryStartingIndex = getLastTitleIndex(titleIndexesArray);

  if (shouldRenderFullWidthPromo({ currentRowNo, isFullWidthInGridPromoHidden })) {
    const indexes = Object.keys(currentRowNo);
    for (let i = 0; i < indexes.length; i += 1) {
      const index = parseInt(indexes[i], 10) - (currentPageNumber - 1) * recordsPerPage;
      const tileSize = getTileSize({ currentRowNo, indexes, i });
      if (index <= gridPosition) {
        adjustedIndex += tileSize;
        if (titleIndexesArray.length > 0) {
          nearestCategoryStartingIndex += tileSize;
        }
      }
    }
  }

  let rowStart = Math.floor(Math.max(0, adjustedIndex - 1) / columns) * columns;
  if (shouldUpdateRowStart({ titleIndexesArray, nearestCategoryStartingIndex, adjustedIndex })) {
    rowStart =
      nearestCategoryStartingIndex +
      Math.floor((adjustedIndex - nearestCategoryStartingIndex) / columns) * columns;
  }

  const noOfElements = gridBlockRef.children.length;
  const rowEnd = Math.min(rowStart + columns, noOfElements);

  for (let i = rowStart; i < rowEnd; i += 1) {
    if (getIsValidProductTileInRow({ i, adjustedIndex, gridBlockRef })) {
      return getTileHeight(gridBlockRef.children[i]);
    }
  }

  return { tileHeightValue: 0, tileBadgeHeightValue: 0 };
};

const EspotPlpPromo = ({
  uniqueId,
  eSpotPromoKey,
  linkedImage,
  isPLPEspot,
  gridPosition,
  gridBlockRef,
  titleIndexesArray,
  currentRowNo,
  isFullWidthInGridPromoHidden,
  currentPageNumber,
  recordsPerPage,
}) => {
  const { extraConfig } = React.useContext(LabelsContext);
  const { image, video, link } = (linkedImage && linkedImage[0]) || {};
  const { isDamImageV2EnabledPLP } = extraConfig || {};
  if (isPLPEspot) {
    const { tileHeightValue, tileBadgeHeightValue } = getEspotTileHeight({
      gridPosition,
      gridBlockRef,
      titleIndexesArray,
      currentRowNo,
      isFullWidthInGridPromoHidden,
      currentPageNumber,
      recordsPerPage,
    });
    if (tileHeightValue) {
      return (
        <StyledESpotPromoTile
          className="espot-plp-tile"
          key={eSpotPromoKey}
          id={uniqueId}
          tileBadgeHeightValue={tileBadgeHeightValue}
        >
          <StyledDamImage
            imgData={image}
            className="espot-promo-dam"
            link={link}
            videoData={video}
            isOptimizedVideo
            isDamImageV2EnabledPLP={isDamImageV2EnabledPLP}
            noLink
            tileHeightValue={tileHeightValue}
            tileBadgeHeightValue={tileBadgeHeightValue}
          />
        </StyledESpotPromoTile>
      );
    }

    return <StyledESpotPromoSkeleton />;
  }
  return (
    <StyledESpotPromoTile className="espot-plp-tile" id={uniqueId} key={eSpotPromoKey}>
      <StyledDamImage
        imgData={image}
        className="espot-promo-dam"
        link={link}
        videoData={video}
        isOptimizedVideo
        isDamImageV2EnabledPLP={isDamImageV2EnabledPLP}
        noLink
      />
    </StyledESpotPromoTile>
  );
};

EspotPlpPromo.propTypes = {
  linkedImage: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.shape({}),
      video: PropTypes.shape({}),
      link: PropTypes.shape({}),
    })
  ).isRequired,
  tileHeightValue: PropTypes.number,
  tileBadgeHeightValue: PropTypes.number,
  isPLPEspot: PropTypes.bool,
};

EspotPlpPromo.defaultProps = {
  tileHeightValue: 0,
  tileBadgeHeightValue: 0,
  isPLPEspot: false,
};

EspotPlpPromo.displayName = 'EspotPlpPromo';

export default SafeComponent(EspotPlpPromo);
export { EspotPlpPromo as EspotPlpPromoVanilla };
